import React, { useState, useRef, useEffect } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { LayoutGrid, MessageSquare, Bell, ChevronDown, LogOut, Settings, UserCircle, ShoppingCart } from "lucide-react";
import { TOP_NAV, LOGO_URL, SOLUTIONS } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import NotificacionesBell from "./NotificacionesBell";

export default function Topbar() {
  const { user, logout } = useAuth();
  const { count: cartCount } = useCart();
  const navigate = useNavigate();
  const [appsOpen, setAppsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const appsRef = useRef(null);
  const profRef = useRef(null);
  const [msgNoLeidos, setMsgNoLeidos] = useState(0);

  useEffect(() => {
    let cancel = false;
    async function refrescar() {
      const { ConexionAPI } = await import("../lib/conexion");
      const n = await ConexionAPI.mensajesNoLeidos();
      if (!cancel) setMsgNoLeidos(n);
    }
    refrescar();
    const t = setInterval(refrescar, 15000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    function onClick(e) {
      if (appsRef.current && !appsRef.current.contains(e.target)) setAppsOpen(false);
      if (profRef.current && !profRef.current.contains(e.target)) setProfileOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "CL";

  return (
    <header
      className="h-20 bg-white border-b border-slate-100 shadow-topbar sticky top-0 z-30 px-5 lg:px-8 flex items-center justify-between gap-2 min-w-0"
      data-testid="topbar"
    >
      {/* Mobile logo (sidebar hidden on small screens) */}
      <Link to="/" className="lg:hidden flex items-center gap-2 min-w-0 shrink" data-testid="topbar-logo-mobile">
        <img src={LOGO_URL} alt="ConstruLink" className="h-9 w-auto max-w-[120px] sm:h-12 sm:max-w-[190px] object-contain" />
      </Link>

      {/* Center nav — se muestra desde 2xl (>=1536). Con el sidebar fijo de 288px,
          en 1024–1535 el ancho útil del main es insuficiente para nav + cluster;
          en esa banda la navegación la provee el sidebar, evitando overflow horizontal. */}
      <nav className="hidden 2xl:flex items-center gap-1 xl:gap-2 flex-1 justify-center 2xl:justify-start 2xl:ml-4">
        {TOP_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            data-testid={`topnav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              [
                "relative px-3 py-2 text-sm font-semibold tracking-wide transition-colors",
                isActive
                  ? "text-brand-red"
                  : "text-slate-700 hover:text-brand-blue",
              ].join(" ")
            }
          >
            {({ isActive }) => (
              <>
                <span className="uppercase">{item.label}</span>
                {isActive && (
                  <span className="absolute left-3 right-3 -bottom-1 h-[3px] rounded-full bg-brand-red" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Right */}
      <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
        {/* Apps launcher */}
        <div className="relative" ref={appsRef}>
          <button
            onClick={() => setAppsOpen((v) => !v)}
            data-testid="topbar-apps-button"
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors text-slate-700"
          >
            <LayoutGrid size={18} />
            <span className="text-sm font-semibold hidden sm:inline">Apps</span>
          </button>
          {appsOpen && (
            <div className="absolute right-0 mt-3 w-[340px] bg-white rounded-2xl shadow-lifted border border-slate-100 p-4 z-40" data-testid="topbar-apps-menu">
              <p className="text-[11px] uppercase tracking-widest font-bold text-slate-400 mb-3 px-1">
                Ecosistema ConstruLink
              </p>
              <div className="grid grid-cols-3 gap-2">
                {SOLUTIONS.map((s) => {
                  const Icon = s.Icon;
                  return (
                    <Link
                      key={s.id}
                      to={s.path}
                      onClick={() => setAppsOpen(false)}
                      className="group flex flex-col items-center justify-center gap-2 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                      data-testid={`apps-menu-${s.id}`}
                    >
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: s.tint, color: s.color }}
                      >
                        <Icon size={20} />
                      </div>
                      <span className="text-[11px] font-semibold text-slate-700 text-center leading-tight">
                        {s.title.split(" ")[0]}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <Link
          to="/conexion/mensajes"
          data-testid="topbar-messages"
          className="relative w-10 h-10 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 flex items-center justify-center text-slate-700"
          aria-label="Mensajes"
        >
          <MessageSquare size={18} />
          {msgNoLeidos > 0 && (
            <span data-testid="topbar-messages-badge" className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-brand-red text-white text-[9px] font-bold flex items-center justify-center">
              {msgNoLeidos > 9 ? "9+" : msgNoLeidos}
            </span>
          )}
        </Link>

        <NotificacionesBell />

        <Link
          to="/tienda/carrito"
          data-testid="topbar-carrito"
          className="relative w-10 h-10 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 flex items-center justify-center text-slate-700"
          aria-label="Carrito"
        >
          <ShoppingCart size={18} />
          {cartCount > 0 && (
            <span data-testid="topbar-carrito-badge" className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-white text-[9px] font-bold flex items-center justify-center" style={{ backgroundColor: "#D97706" }}>
              {cartCount > 9 ? "9+" : cartCount}
            </span>
          )}
        </Link>

        {/* Profile */}
        <div className="relative" ref={profRef}>
          <button
            onClick={() => setProfileOpen((v) => !v)}
            data-testid="topbar-profile-button"
            className="flex items-center gap-2.5 pl-1 pr-2.5 py-1 rounded-full hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors"
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-blue to-brand-navy text-white text-sm font-bold flex items-center justify-center shadow-inner">
              {initials}
            </div>
            <div className="text-left hidden lg:block">
              <p className="text-sm font-semibold text-slate-800 leading-tight">
                {user?.name || "Usuario"}
              </p>
              <p className="text-[11px] uppercase tracking-wider text-slate-400">
                {user?.role || "cliente"}
              </p>
            </div>
            <ChevronDown size={16} className="text-slate-500 hidden sm:block" />
          </button>
          {profileOpen && (
            <div className="absolute right-0 mt-3 w-64 bg-white rounded-2xl shadow-lifted border border-slate-100 p-2 z-40" data-testid="topbar-profile-menu">
              <div className="px-3 py-3 border-b border-slate-100">
                <p className="text-sm font-semibold text-slate-800">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <Link to="/mi-perfil" className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-lg" onClick={() => setProfileOpen(false)} data-testid="topbar-mi-perfil">
                <UserCircle size={16} /> Mi perfil
              </Link>
              <Link to="/tienda/mis-compras" className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-lg" onClick={() => setProfileOpen(false)} data-testid="topbar-mis-compras">
                <ShoppingCart size={16} /> Mis Compras
              </Link>
              <Link to="/configuracion" className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-lg" onClick={() => setProfileOpen(false)} data-testid="topbar-configuracion">
                <Settings size={16} /> Configuración
              </Link>
              <button
                data-testid="topbar-logout"
                onClick={async () => {
                  setProfileOpen(false);
                  await logout();
                  navigate("/login");
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-brand-red hover:bg-red-50 rounded-lg"
              >
                <LogOut size={16} /> Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
